import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Checkbox,
} from "@mui/material";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import ImageIcon from "@mui/icons-material/Image";
import LayersIcon from "@mui/icons-material/Layers";
import CropFreeIcon from "@mui/icons-material/CropFree";
import PhotoIcon from "@mui/icons-material/Photo";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import BusinessIcon from "@mui/icons-material/Business";
import BadgeIcon from "@mui/icons-material/Badge";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";


import { API_URL, API_UPLOAD_URL } from "./config";

const SNAP_THRESHOLD = 6;

const IdCardTemplateManager = () => {
  // =========================
  // Theme
  // =========================
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";
  const BLUE = "rgba(25,118,210,0.95)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 3,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
    boxShadow: "0 8px 30px rgba(15,23,42,0.06)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };
  const surfaceSx = {
    borderRadius: 3,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
    boxShadow: "0 4px 18px rgba(15,23,42,0.04)",
  };
  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };
  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    boxShadow: "none",
    "&:hover": { backgroundColor: THEME_ORANGE_2, boxShadow: "none" },
  };
  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: BLUE,
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };
  const tinyIconBtnSx = {
    color: BLUE,
    border: "1px solid rgba(25,118,210,0.18)",
    borderRadius: 2,
    "&:hover": {
      backgroundColor: "rgba(240,120,40,0.08)",
      color: THEME_ORANGE,
      borderColor: "rgba(240,120,40,0.25)",
    },
  };
  const tableWrapSx = {
    ...surfaceSx,
    mt: 2,
    overflow: "hidden",
  };
  const theadRowSx = {
    backgroundColor: BLUE,
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
    },
  };
  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
  });

  // =========================
  // Constants
  // =========================
  const token = localStorage.getItem("auth");
  const [editRowId, setEditRowId] = useState(null);
  const previewScale = 1;

  const COLOR_PALETTE = [
    { label: "Portal Orange", value: "#F07828" },
    { label: "Deep Blue", value: "#1565C0" },
    { label: "Royal Blue", value: "#1976D2" },
    { label: "Navy", value: "#0D47A1" },
    { label: "Teal", value: "#00897B" },
    { label: "Green", value: "#2E7D32" },
    { label: "Maroon", value: "#8E244D" },
    { label: "Purple", value: "#6A1B9A" },
    { label: "Gray", value: "#546E7A" },
    { label: "Black", value: "#263238" },
    { label: "Red", value: "#C62828" },
    { label: "White", value: "#FFFFFF" },
  ];

  const AVAILABLE_FIELDS = {
    STUDENT: [
      "PersonName",
      "PersonId",
      "ProgramName",
      "BranchName",
      "SectionName",
      "BloodGroup",
      "DOB",
      "Phone",
      "Address",
      "EmergencyPhone",
      "ValidUpto",
    ],
    EMPLOYEE: [
      "PersonName",
      "PersonId",
      "Designation",
      "DepartmentName",
      "BloodGroup",
      "DOB",
      "Phone",
      "Address",
      "EmergencyPhone",
      "ValidUpto",
    ],
  };

  const SAMPLE_DATA = {
    CampusName: "KIET Group of Engineering Colleges and Research Campus",
    PersonName: "Aishwarya Rao",
    PersonId: "22A91A0501",
    ProgramName: "B.Tech",
    BranchName: "Computer Science and Engineering",
    SectionName: "A",
    BloodGroup: "O+",
    DOB: "14-08-2004",
    Phone: "9876543210",
    Address: "Hyderabad, Telangana, India",
    EmergencyPhone: "9123456789",
    ValidUpto: "31-05-2027",
    Designation: "Assistant Professor",
    DepartmentName: "Computer Science and Engineering",
    PassportPhoto: "",
  };

  const SIDE_OBJECT_OPTIONS = [
    { key: "collegeName", label: "College Name", icon: <BusinessIcon fontSize="small" /> },
    { key: "photo", label: "Photo", icon: <PhotoIcon fontSize="small" /> },
    { key: "logo", label: "Logo", icon: <BadgeIcon fontSize="small" /> },
    { key: "qr", label: "QR", icon: <QrCode2Icon fontSize="small" /> },
    { key: "header", label: "Header", icon: <CropFreeIcon fontSize="small" /> },
    { key: "footer", label: "Footer", icon: <CropFreeIcon fontSize="small" /> },
    { key: "freeText", label: "Free Text", icon: <TextFieldsIcon fontSize="small" /> },
    { key: "freeImage", label: "Free Image", icon: <ImageIcon fontSize="small" /> },
  ];

  // =========================
  // Defaults
  // =========================
  const getDefaultDimensions = (orientation = "PORTRAIT") => {
    return orientation === "LANDSCAPE"
      ? { CardWidth: 360, CardHeight: 220 }
      : { CardWidth: 220, CardHeight: 360 };
  };

  const getDefaultFrontLayout = (cardType, orientation = "PORTRAIT") => {
    const { CardWidth, CardHeight } = getDefaultDimensions(orientation);
    const isLandscape = orientation === "LANDSCAPE";
  
    return {
      orientation,
      cardWidth: CardWidth,
      cardHeight: CardHeight,
      headerHeight: 48,
      footerHeight: 28,
      headerColor: "#F07828",
      footerColor: "#1565C0",
      accentColor: "#1565C0",
      textColor: "#1F2937",
      backgroundImage: "",
      logoImage: "",
      showLogo: true,
      showPhoto: true,
      showQr: false,
      freeTexts: [],
      freeImages: [],
  
      footer: {
        x: 8,
        y: CardHeight - 24,
        w: CardWidth - 16,
        h: 18,
        text: "",
        fontSize: 10,
        fontWeight: 700,
        align: "center",
        color: "#FFFFFF",
      },
  
      photo: isLandscape
        ? { x: 18, y: 62, w: 76, h: 88 }
        : { x: 16, y: 58, w: 78, h: 92 },
  
      logo: { x: 12, y: 8, w: 34, h: 34 },
      qr: { x: CardWidth - 90, y: 58, w: 72, h: 72 },
      collegeName: {
        x: 52,
        y: 7,
        w: CardWidth - 112,
        h: 34,
        fontSize: 13,
        fontWeight: 800,
        align: "center",
        lineClamp: 2,
        color: "#FFFFFF",
      },
  
      fields:
        cardType === "EMPLOYEE"
          ? [
              { key: "PersonName", label: "Name", visible: true, x: 104, y: 62, w: 215, h: 18, fontSize: 14, fontWeight: 800, color: "#1F2937" },
              { key: "PersonId", label: "Employee ID", visible: true, x: 104, y: 84, w: 215, h: 16, fontSize: 11, fontWeight: 600, color: "#1F2937" },
              { key: "Designation", label: "Designation", visible: true, x: 104, y: 104, w: 215, h: 16, fontSize: 11, fontWeight: 500, color: "#1F2937" },
              { key: "DepartmentName", label: "Department", visible: true, x: 104, y: 122, w: 215, h: 16, fontSize: 11, fontWeight: 500, color: "#1F2937" },
              { key: "BloodGroup", label: "Blood Group", visible: true, x: 104, y: 142, w: 95, h: 16, fontSize: 11, fontWeight: 500, color: "#1F2937" },
              { key: "ValidUpto", label: "Valid Upto", visible: true, x: 205, y: 142, w: 110, h: 16, fontSize: 11, fontWeight: 500, color: "#1F2937" },
            ]
          : [
              { key: "PersonName", label: "Name", visible: true, x: 104, y: 62, w: 215, h: 18, fontSize: 14, fontWeight: 800, color: "#1F2937" },
              { key: "PersonId", label: "Student ID", visible: true, x: 104, y: 84, w: 215, h: 16, fontSize: 11, fontWeight: 600, color: "#1F2937" },
              { key: "ProgramName", label: "Program", visible: true, x: 104, y: 104, w: 215, h: 16, fontSize: 11, fontWeight: 500, color: "#1F2937" },
              { key: "BranchName", label: "Branch", visible: true, x: 104, y: 122, w: 215, h: 16, fontSize: 11, fontWeight: 500, color: "#1F2937" },
              { key: "SectionName", label: "Section", visible: true, x: 104, y: 140, w: 215, h: 16, fontSize: 11, fontWeight: 500, color: "#1F2937" },
              { key: "BloodGroup", label: "Blood Group", visible: true, x: 104, y: 158, w: 95, h: 16, fontSize: 11, fontWeight: 500, color: "#1F2937" },
              { key: "ValidUpto", label: "Valid Upto", visible: true, x: 205, y: 158, w: 110, h: 16, fontSize: 11, fontWeight: 500, color: "#1F2937" },
            ],
    };
  };

  const getDefaultBackLayout = (cardType, orientation = "PORTRAIT") => {
    const { CardWidth, CardHeight } = getDefaultDimensions(orientation);
  
    return {
      orientation,
      cardWidth: CardWidth,
      cardHeight: CardHeight,
      headerHeight: 48,
      footerHeight: 28,
      headerColor: "#1565C0",
      footerColor: "#F07828",
      accentColor: "#1565C0",
      textColor: "#1F2937",
      backgroundImage: "",
      logoImage: "",
      showLogo: false,
      showPhoto: false,
      showQr: true,
      freeTexts: [],
      freeImages: [],
  
      footer: {
        x: 8,
        y: CardHeight - 24,
        w: CardWidth - 16,
        h: 18,
        text: "",
        fontSize: 10,
        fontWeight: 700,
        align: "center",
        color: "#FFFFFF",
      },
  
      qr: { x: CardWidth - 90, y: 58, w: 72, h: 72 },
      logo: { x: 12, y: 8, w: 34, h: 34 },
  
      collegeName: {
        x: 16,
        y: 8,
        w: CardWidth - 32,
        h: 34,
        fontSize: 13,
        fontWeight: 800,
        align: "center",
        lineClamp: 2,
        color: "#FFFFFF",
      },
  
      fields: [
        { key: "DOB", label: "DOB", visible: true, x: 16, y: 58, w: 180, h: 16, fontSize: 11, fontWeight: 500, color: "#1F2937" },
        { key: "Phone", label: "Phone", visible: true, x: 16, y: 78, w: 180, h: 16, fontSize: 11, fontWeight: 500, color: "#1F2937" },
        { key: "Address", label: "Address", visible: true, x: 16, y: 98, w: 180, h: 34, fontSize: 10, fontWeight: 500, color: "#1F2937" },
        { key: "EmergencyPhone", label: "Emergency", visible: true, x: 16, y: 138, w: 180, h: 16, fontSize: 11, fontWeight: 500, color: "#1F2937" },
        { key: "ValidUpto", label: "Valid Upto", visible: true, x: 16, y: 158, w: 180, h: 16, fontSize: 11, fontWeight: 500, color: "#1F2937" },
      ],
    };
  };

  // =========================
  // State
  // =========================
  const [logincampusCode, setLogincampusCode] = useState("");
  const [rows, setRows] = useState([]);
  const [loadingLookups, setLoadingLookups] = useState(false);
  const [options, setOptions] = useState({SubCampusCode: [], });
  const [selectedSide, setSelectedSide] = useState("FRONT");
  const [selectedFieldIndex, setSelectedFieldIndex] = useState(null);
  const [selectedObjectKey, setSelectedObjectKey] = useState(null);
  const [interaction, setInteraction] = useState(null);
  const [guides, setGuides] = useState({ vertical: null, horizontal: null });
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [error, setError] = useState("");

  const canvasRefs = {
    FRONT: useRef(null),
    BACK: useRef(null),
  };

  const [form, setForm] = useState(() => ({
    CampusCode: "",
    SubCampusCode: "",
    TemplateCode: "",
    TemplateName: "",
    CardType: "STUDENT",
    Orientation: "LANDSCAPE",
    CardWidth: getDefaultDimensions("LANDSCAPE").CardWidth,
    CardHeight: getDefaultDimensions("LANDSCAPE").CardHeight,
    IsDefault: true,
    FrontLayoutJson: getDefaultFrontLayout("STUDENT", "LANDSCAPE"),
    BackLayoutJson: getDefaultBackLayout("STUDENT", "LANDSCAPE"),
  }));

  // =========================
  // Token / API
  // =========================
  useEffect(() => {
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const cc =
        payload?.logincampuscode ||
        payload?.campusCode ||
        payload?.CampusCode ||
        "";
      setLogincampusCode(cc);
      setForm((s) => ({ ...s, CampusCode: cc }));
    } catch {
      setError("Failed to decode auth token");
    }
  }, [token]);

  const authConfig = useMemo(
    () => ({
      headers: { Authorization: `Bearer ${token}` },
    }),
    [token]
  );

  const getAssetUrl = (filePath) => {
    if (!filePath) return "";

    const value = String(filePath).trim();

    if (!value) return "";
    if (value.startsWith("data:")) return value;
    if (/^https?:\/\//i.test(value)) return value;

    const clean = value.replace(/^\/+/, "");

    // If backend already returned uploads/xxx
    if (clean.startsWith("uploads/")) {
      return `${API_UPLOAD_URL}/${clean}`;
    }

    // Normal stored DB value:
    // UNFLO/IdCardTemplates/StudentID/FRONT/BACKGROUND_xxx.jpg
    return `${API_UPLOAD_URL}/uploads/${clean}`;
  };

  const getPassportPhotoUrl = (passportPhoto) => {
    if (!passportPhoto) return "";

    const value = String(passportPhoto);

    if (value.startsWith("data:")) return value;
    if (value.startsWith("http://") || value.startsWith("https://")) return value;

    return `${API_UPLOAD_URL}/uploads/${value.replace(/^\/+/, "")}`;
  };

  const uploadTemplateAsset = async (file, side, assetType) => {
    if (!file) return "";

    const fd = new FormData();
    fd.append("templateAsset", file);
    fd.append("TemplateCode", form.TemplateCode || "TEMP");
    fd.append("Side", side);
    fd.append("AssetType", assetType);

    const { data } = await axios.post(
      `${API_URL}/api/idcards/templates/upload-asset`,
      fd,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return data.filename;
  };

  useEffect(() => {
    if (!logincampusCode) return;
  
    let cancelled = false;
  
    (async () => {
      try {
        setLoadingLookups(true);
  
        const { data } = await axios.get(`${API_URL}/api/lookups/all`, {
          ...authConfig,
          params: {
            CampusCode: logincampusCode,
          },
        });
  
        if (cancelled) return;
  
        setOptions({
          SubCampusCode: data?.SubCampusCode || [],
        });
      } catch (e) {
        console.error(e);
        setError("Failed to load Sub Campus dropdown");
      } finally {
        if (!cancelled) setLoadingLookups(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [logincampusCode, authConfig]);

      const getSubCampusValue = (sc) => {
      if (typeof sc === "string") return sc;
    
      return (
        sc?.SubCampusCode ||
        sc?.subCampusCode ||
        sc?.SubcampusCode ||
        sc?.value ||
        sc?.code ||
        ""
      );
    };
  

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/idcards/templates`, {
        ...authConfig,
        params: { CampusCode: logincampusCode },
      });
      setRows(response.data || []);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load templates");
    }
  };

  useEffect(() => {
    if (!logincampusCode) return;
    loadTemplates();
  }, [logincampusCode]);

  const safeParseJson = (value, fallback = {}) => {
    if (!value) return fallback;
    if (typeof value === "object") return value;
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  };

  const ensureObjectExists = (layout, objectKey, side, orientation = form.Orientation) => {
    const defaults =
      side === "FRONT"
        ? getDefaultFrontLayout(form.CardType, orientation)
        : getDefaultBackLayout(form.CardType, orientation);
  
    const next = { ...layout };
  
    if (objectKey === "logo") {
      next.showLogo = true;
      next.logo = next.logo || defaults.logo;
      next.logoImage = next.logoImage ?? defaults.logoImage ?? "";
    }
  
    if (objectKey === "photo") {
      next.showPhoto = true;
      next.photo = next.photo || defaults.photo;
    }
  
    if (objectKey === "qr") {
      next.showQr = true;
      next.qr = next.qr || defaults.qr;
    }
  
    if (objectKey === "collegeName") {
      next.collegeName = next.collegeName || defaults.collegeName;
    }
  
    if (objectKey === "header") {
      next.headerDeleted = false;
      next.headerHeight = Number(next.headerHeight ?? 0) > 0
        ? Number(next.headerHeight)
        : Number(defaults.headerHeight ?? 48);
      next.headerColor = next.headerColor || defaults.headerColor;
    }

    if (objectKey === "footer") {
      next.footerDeleted = false;
      next.footerHeight = Number(next.footerHeight ?? 0) > 0
        ? Number(next.footerHeight)
        : Number(defaults.footerHeight ?? 28);
      next.footerColor = next.footerColor || defaults.footerColor;
      next.footer = next.footer || defaults.footer;
    }
  
    return next;
  };
  
  const loadTemplateIntoDesigner = (row) => {
    const orientation = row?.Orientation || "PORTRAIT";
    const dims = getDefaultDimensions(orientation);
  
    const front = safeParseJson(
      row?.FrontLayoutJson,
      getDefaultFrontLayout(row?.CardType || "STUDENT", orientation)
    );
  
    const back = safeParseJson(
      row?.BackLayoutJson,
      getDefaultBackLayout(row?.CardType || "STUDENT", orientation)
    );
  
    const cardWidth =
      Number(front?.cardWidth) ||
      Number(back?.cardWidth) ||
      Number(row?.CardWidth) ||
      dims.CardWidth;
  
    const cardHeight =
      Number(front?.cardHeight) ||
      Number(back?.cardHeight) ||
      Number(row?.CardHeight) ||
      dims.CardHeight;
  
    setForm({
      CampusCode: logincampusCode,
      SubCampusCode: row?.SubCampusCode || "",
      TemplateCode: row?.TemplateCode || "",
      TemplateName: row?.TemplateName || "",
      CardType: row?.CardType || "STUDENT",
      Orientation: orientation,
      CardWidth: cardWidth,
      CardHeight: cardHeight,
      IsDefault: !!row?.IsDefault,
      FrontLayoutJson: {
        ...getDefaultFrontLayout(row?.CardType || "STUDENT", orientation),
        ...front,
        orientation,
        cardWidth,
        cardHeight,
      },
      BackLayoutJson: {
        ...getDefaultBackLayout(row?.CardType || "STUDENT", orientation),
        ...back,
        orientation,
        cardWidth,
        cardHeight,
      },
    });
  
    setSelectedSide("FRONT");
    setSelectedFieldIndex(null);
    setSelectedObjectKey(null);
  };
  
  const handleEditTemplate = (row) => {
    setEditRowId(row.TemplateId);
    loadTemplateIntoDesigner(row);
  };

  const handleTemplateInputChange = (e, templateId = null) => {
  const { name, type, checked, value } = e.target;
  const val = type === "checkbox" ? (checked ? 1 : 0) : value;

    setRows((prev) =>
      prev.map((r) =>
        r.TemplateId === templateId ? { ...r, [name]: val } : r
      )
    );
  };

  const handleUpdateRow = async (templateId) => {
    const prevRows = rows;
    const updated = rows.find((r) => r.TemplateId === templateId);
  
    try {
      const payload = {
        ...updated,
        CampusCode: logincampusCode,
        IsDefault: Number(updated.IsDefault) ? 1 : 0,
        IsActive: Number(updated.IsActive) ? 1 : 0,
      };
  
      if (editRowId === templateId) {
        payload.TemplateCode = form.TemplateCode;
        payload.TemplateName = form.TemplateName;
        payload.CardType = form.CardType;
        payload.Orientation = form.Orientation;
        payload.CardWidth = form.CardWidth;
        payload.CardHeight = form.CardHeight;
        payload.FrontLayoutJson = {
          ...form.FrontLayoutJson,
          orientation: form.Orientation,
          cardWidth: form.CardWidth,
          cardHeight: form.CardHeight,
        };
        payload.BackLayoutJson = {
          ...form.BackLayoutJson,
          orientation: form.Orientation,
          cardWidth: form.CardWidth,
          cardHeight: form.CardHeight,
        };
        payload.IsDefault = form.IsDefault ? 1 : 0;
      }
  
      await axios.put(
        `${API_URL}/api/idcards/templates/${templateId}`,
        payload,
        authConfig
      );
  
      setSnack({ open: true, message: "Template updated", severity: "success" });
      setEditRowId(null);
      await loadTemplates();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to update template");
      setRows(prevRows);
    }
  };

const handleDeleteRow = async (templateId) => {
  const prevRows = rows;
  try {
    await axios.delete(`${API_URL}/api/idcards/templates/${templateId}`, authConfig);
    setSnack({ open: true, message: "Template deleted", severity: "success" });
    await loadTemplates();
  } catch (err) {
    setError(err?.response?.data?.error || "Failed to delete template");
    setRows(prevRows);
  }
};

  // =========================
  // Layout Helpers
  // =========================
  const currentLayout = selectedSide === "FRONT" ? form.FrontLayoutJson : form.BackLayoutJson;
  const currentFields = currentLayout?.fields || [];
  const [hoveredObjectKey, setHoveredObjectKey] = useState(null);

  const updateLayout = (side, updater) => {
    setForm((s) => {
      const key = side === "FRONT" ? "FrontLayoutJson" : "BackLayoutJson";
      return {
        ...s,
        [key]: updater(s[key]),
      };
    });
  };

  const updateFormCardSize = (width, height) => {
    const safeWidth = Math.max(220, Number(width || 0));
    const safeHeight = Math.max(140, Number(height || 0));
  
    setForm((s) => ({
      ...s,
      CardWidth: safeWidth,
      CardHeight: safeHeight,
      FrontLayoutJson: {
        ...s.FrontLayoutJson,
        cardWidth: safeWidth,
        cardHeight: safeHeight,
        collegeName: s.FrontLayoutJson?.collegeName
          ? {
              ...s.FrontLayoutJson.collegeName,
              w: Math.max(140, safeWidth - 112),
            }
          : s.FrontLayoutJson?.collegeName,
        qr: s.FrontLayoutJson?.qr
          ? { ...s.FrontLayoutJson.qr, x: Math.max(0, safeWidth - 90) }
          : s.FrontLayoutJson?.qr,
        footer: s.FrontLayoutJson?.footer
          ? {
              ...s.FrontLayoutJson.footer,
              y: safeHeight - 24,
              w: safeWidth - 16,
            }
          : s.FrontLayoutJson?.footer,
      },
      BackLayoutJson: {
        ...s.BackLayoutJson,
        cardWidth: safeWidth,
        cardHeight: safeHeight,
        collegeName: s.BackLayoutJson?.collegeName
          ? {
              ...s.BackLayoutJson.collegeName,
              w: Math.max(140, safeWidth - 32),
            }
          : s.BackLayoutJson?.collegeName,
        qr: s.BackLayoutJson?.qr
          ? { ...s.BackLayoutJson.qr, x: Math.max(0, safeWidth - 90) }
          : s.BackLayoutJson?.qr,
        footer: s.BackLayoutJson?.footer
          ? {
              ...s.BackLayoutJson.footer,
              y: safeHeight - 24,
              w: safeWidth - 16,
            }
          : s.BackLayoutJson?.footer,
      },
    }));
  };

  const handleCardTypeChange = (cardType) => {
    setForm((s) => {
      const front = getDefaultFrontLayout(cardType, s.Orientation);
      const back = getDefaultBackLayout(cardType, s.Orientation);
  
      return {
        ...s,
        CardType: cardType,
        FrontLayoutJson: {
          ...front,
          ...s.FrontLayoutJson,
          fields: front.fields,
          cardWidth: s.CardWidth,
          cardHeight: s.CardHeight,
          backgroundImage: s.FrontLayoutJson?.backgroundImage || "",
          logoImage: s.FrontLayoutJson?.logoImage || "",
          footer: {
            ...front.footer,
            ...(s.FrontLayoutJson?.footer || {}),
            y: s.CardHeight - 24,
            w: s.CardWidth - 16,
          },
        },
        BackLayoutJson: {
          ...back,
          ...s.BackLayoutJson,
          fields: back.fields,
          cardWidth: s.CardWidth,
          cardHeight: s.CardHeight,
          backgroundImage: s.BackLayoutJson?.backgroundImage || "",
          logoImage: s.BackLayoutJson?.logoImage || "",
          footer: {
            ...back.footer,
            ...(s.BackLayoutJson?.footer || {}),
            y: s.CardHeight - 24,
            w: s.CardWidth - 16,
          },
        },
      };
    });
  
    setSelectedFieldIndex(null);
    setSelectedObjectKey(null);
  };

  const handleOrientationChange = (orientation) => {
    const { CardWidth, CardHeight } = getDefaultDimensions(orientation);
  
    setForm((s) => {
      const front = getDefaultFrontLayout(s.CardType, orientation);
      const back = getDefaultBackLayout(s.CardType, orientation);
  
      return {
        ...s,
        Orientation: orientation,
        CardWidth,
        CardHeight,
        FrontLayoutJson: {
          ...front,
          ...s.FrontLayoutJson,
          orientation,
          cardWidth: CardWidth,
          cardHeight: CardHeight,
          backgroundImage: s.FrontLayoutJson?.backgroundImage || "",
          logoImage: s.FrontLayoutJson?.logoImage || "",
          headerColor: s.FrontLayoutJson?.headerColor || front.headerColor,
          footerColor: s.FrontLayoutJson?.footerColor || front.footerColor,
          accentColor: s.FrontLayoutJson?.accentColor || front.accentColor,
          textColor: s.FrontLayoutJson?.textColor || front.textColor,
          photo: s.FrontLayoutJson?.showPhoto === false
            ? s.FrontLayoutJson?.photo
            : (s.FrontLayoutJson?.photo || front.photo),
          logo: s.FrontLayoutJson?.logo || front.logo,
          collegeName: {
            ...front.collegeName,
            ...(s.FrontLayoutJson?.collegeName || {}),
          },
          footer: {
            ...front.footer,
            ...(s.FrontLayoutJson?.footer || {}),
            y: CardHeight - 24,
            w: CardWidth - 16,
          },
        },
        BackLayoutJson: {
          ...back,
          ...s.BackLayoutJson,
          orientation,
          cardWidth: CardWidth,
          cardHeight: CardHeight,
          backgroundImage: s.BackLayoutJson?.backgroundImage || "",
          logoImage: s.BackLayoutJson?.logoImage || "",
          headerColor: s.BackLayoutJson?.headerColor || back.headerColor,
          footerColor: s.BackLayoutJson?.footerColor || back.footerColor,
          accentColor: s.BackLayoutJson?.accentColor || back.accentColor,
          textColor: s.BackLayoutJson?.textColor || back.textColor,
          qr: s.BackLayoutJson?.showQr === false
            ? s.BackLayoutJson?.qr
            : (s.BackLayoutJson?.qr || back.qr),
          logo: s.BackLayoutJson?.logo || back.logo,
          collegeName: {
            ...back.collegeName,
            ...(s.BackLayoutJson?.collegeName || {}),
          },
          footer: {
            ...back.footer,
            ...(s.BackLayoutJson?.footer || {}),
            y: CardHeight - 24,
            w: CardWidth - 16,
          },
        },
      };
    });
  
    setSelectedFieldIndex(null);
    setSelectedObjectKey(null);
  };

  const handleImageUpload = async (side, targetKey, file) => {
    try {
      if (!file) return;

      const assetType =
        targetKey === "logoImage"
          ? "LOGO"
          : targetKey === "backgroundImage"
          ? "BACKGROUND"
          : "IMAGE";

      const filename = await uploadTemplateAsset(file, side, assetType);

      if (!filename) return;

      updateLayout(side, (layout) => ({
        ...layout,
        [targetKey]: filename,
      }));

      setSnack({
        open: true,
        message:
          assetType === "LOGO"
            ? "Logo uploaded successfully"
            : "Image uploaded successfully",
        severity: "success",
      });
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.error || "Failed to upload image");
    }
  };

  const handleSideFieldChange = (idx, key, value) => {
    updateLayout(selectedSide, (layout) => {
      const next = [...(layout?.fields || [])];
      next[idx] = { ...next[idx], [key]: value };
      return { ...layout, fields: next };
    });
  };

  const handleRemoveField = (idx) => {
    updateLayout(selectedSide, (layout) => {
      const next = [...(layout?.fields || [])];
      next.splice(idx, 1);
      return { ...layout, fields: next };
    });
    setSelectedFieldIndex(null);
  };

  const handleAddField = (fieldKey) => {
    if (!fieldKey) return;

    updateLayout(selectedSide, (layout) => {
      const existing = layout?.fields || [];
      if (existing.some((f) => f.key === fieldKey)) return layout;

      return {
        ...layout,
        fields: [
          ...existing,
          {
            key: fieldKey,
            label: fieldKey,
            visible: true,
            x: 100,
            y: 90 + existing.length * 18,
            w: 180,
            h: 16,
            fontSize: 11,
            fontWeight: 500,
            color: layout?.textColor || "#1F2937",
          },
        ],
      };
    });
  };

  const handleAddFreeText = () => {
    updateLayout(selectedSide, (layout) => ({
      ...layout,
      fields: [
        ...(layout.fields || []),
        {
          key: `FREE_TEXT_${Date.now()}`,
          type: "FREE_TEXT",
          label: "",
          value: "Free Text",
          visible: true,
          x: 24,
          y: 190,
          w: 150,
          h: 24,
          fontSize: 12,
          fontWeight: 700,
          color: layout?.textColor || "#1F2937",
        },
      ],
    }));
  };

  const handleAddFreeImage = () => {
    updateLayout(selectedSide, (layout) => ({
      ...layout,
      fields: [
        ...(layout.fields || []),
        {
          key: `FREE_IMAGE_${Date.now()}`,
          type: "FREE_IMAGE",
          label: "",
          image: "",
          visible: true,
          x: 24,
          y: 220,
          w: 70,
          h: 70,
        },
      ],
    }));
  };

  const sampleFieldValue = (fieldKey) => SAMPLE_DATA[fieldKey] || "";

  const getFieldRect = (field) => ({
    x: Number(field?.x || 0),
    y: Number(field?.y || 0),
    w: Number(field?.w || 0),
    h: Number(field?.h || 0),
  });

  const getObjectRect = (layout, objectKey) => {
    if (objectKey === "photo") return { ...(layout.photo || { x: 0, y: 0, w: 0, h: 0 }) };
    if (objectKey === "logo") return { ...(layout.logo || { x: 0, y: 0, w: 0, h: 0 }) };
    if (objectKey === "qr") return { ...(layout.qr || { x: 0, y: 0, w: 0, h: 0 }) };
    if (objectKey === "collegeName") return { ...(layout.collegeName || { x: 0, y: 0, w: 0, h: 0 }) };

    if (objectKey === "header") {
      return {
        x: 0,
        y: 0,
        w: Number(form.CardWidth),
        h: Number(layout.headerHeight ?? 48),
      };
    }

    if (objectKey === "footer") {
      if (!layout.footer || Number(layout.footerHeight ?? 0) <= 0) {
        return { x: 0, y: 0, w: 0, h: 0 };
      }

      return layout.footer;
    }

    return { x: 0, y: 0, w: 0, h: 0 };
  };

  const getComparisonRects = (side, exclude) => {
    const layout = side === "FRONT" ? form.FrontLayoutJson : form.BackLayoutJson;
    const rects = [];

    (layout.fields || []).forEach((f, idx) => {
      if (exclude?.type === "field" && exclude?.idx === idx) return;
      rects.push({ ...getFieldRect(f), kind: "field" });
    });

    ["photo", "logo", "qr", "collegeName", "header", "footer"].forEach((key) => {
      if (exclude?.type === "object" && exclude?.key === key) return;
      if (key === "photo" && !layout.showPhoto) return;
      if (key === "logo" && !layout.showLogo) return;
      if (key === "qr" && !layout.showQr) return;
      rects.push({ ...getObjectRect(layout, key), kind: key });
    });

    rects.push({ x: 0, y: 0, w: Number(form.CardWidth), h: Number(form.CardHeight), kind: "canvas" });
    return rects;
  };

  const snapRect = (candidate, side, exclude) => {
    let next = { ...candidate };
    let vertical = null;
    let horizontal = null;
    const rects = getComparisonRects(side, exclude);

    const targetXValues = [
      { line: next.x, apply: (v) => { next.x = v; } },
      { line: next.x + next.w, apply: (v) => { next.x = v - next.w; } },
      { line: next.x + next.w / 2, apply: (v) => { next.x = v - next.w / 2; } },
    ];

    const targetYValues = [
      { line: next.y, apply: (v) => { next.y = v; } },
      { line: next.y + next.h, apply: (v) => { next.y = v - next.h; } },
      { line: next.y + next.h / 2, apply: (v) => { next.y = v - next.h / 2; } },
    ];

    rects.forEach((r) => {
      const xCandidates = [r.x, r.x + r.w, r.x + r.w / 2];
      const yCandidates = [r.y, r.y + r.h, r.y + r.h / 2];

      targetXValues.forEach((t) => {
        xCandidates.forEach((xv) => {
          if (Math.abs(t.line - xv) <= SNAP_THRESHOLD) {
            t.apply(xv);
            vertical = xv;
          }
        });
      });

      targetYValues.forEach((t) => {
        yCandidates.forEach((yv) => {
          if (Math.abs(t.line - yv) <= SNAP_THRESHOLD) {
            t.apply(yv);
            horizontal = yv;
          }
        });
      });
    });

    next.x = Math.max(0, Math.min(next.x, Number(form.CardWidth) - Math.max(8, next.w)));
    next.y = Math.max(0, Math.min(next.y, Number(form.CardHeight) - Math.max(8, next.h)));
    next.w = Math.max(12, Math.min(next.w, Number(form.CardWidth) - next.x));
    next.h = Math.max(12, Math.min(next.h, Number(form.CardHeight) - next.y));

    return { rect: next, guides: { vertical, horizontal } };
  };

  // =========================
  // Drag / Resize Engine
  // =========================
  useEffect(() => {
    const onMove = (e) => {
      if (!interaction) return;
      const canvas = canvasRefs[interaction.side]?.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const x = (e.clientX - rect.left) / previewScale;
      const y = (e.clientY - rect.top) / previewScale;

      const dx = x - interaction.startMouseX;
      const dy = y - interaction.startMouseY;

      const applyRectChange = (side, nextRect, exclude) => {
        const snapped = snapRect(nextRect, side, exclude);
        setGuides(snapped.guides);

        if (interaction.targetType === "field") {
          updateLayout(side, (layout) => {
            const fields = [...(layout.fields || [])];
            fields[interaction.targetIndex] = {
              ...fields[interaction.targetIndex],
              x: Math.round(snapped.rect.x),
              y: Math.round(snapped.rect.y),
              w: Math.round(snapped.rect.w),
              h: Math.round(snapped.rect.h),
            };
            return { ...layout, fields };
          });
        } else if (interaction.targetType === "object") {
          updateLayout(side, (layout) => {
            if (interaction.objectKey === "header") {
              return {
                ...layout,
                headerHeight: Math.max(0, Math.round(snapped.rect.h)),
                headerDeleted: false,
              };
            }
            
            if (interaction.objectKey === "footer") {
              return {
                ...layout,
                footer: {
                  ...(layout.footer || {}),
                  x: Math.round(snapped.rect.x),
                  y: Math.round(snapped.rect.y),
                  w: Math.round(snapped.rect.w),
                  h: Math.round(snapped.rect.h),
                },
                footerHeight: Math.max(0, Math.round(snapped.rect.h) + 8),
                footerDeleted: false,
              };
            }

            return {
              ...layout,
              [interaction.objectKey]: {
                ...(layout[interaction.objectKey] || {}),
                x: Math.round(snapped.rect.x),
                y: Math.round(snapped.rect.y),
                w: Math.round(snapped.rect.w),
                h: Math.round(snapped.rect.h),
              },
            };
          });
        }
      };

      if (interaction.mode === "move") {
        const nextRect = {
          ...interaction.startRect,
          x: interaction.startRect.x + dx,
          y: interaction.startRect.y + dy,
        };
        applyRectChange(interaction.side, nextRect, interaction.exclude);
      }

      if (interaction.mode === "resize") {
        let next = { ...interaction.startRect };
        const handle = interaction.handle;

        if (handle.includes("e")) next.w = interaction.startRect.w + dx;
        if (handle.includes("s")) next.h = interaction.startRect.h + dy;

        if (handle.includes("w")) {
          next.x = interaction.startRect.x + dx;
          next.w = interaction.startRect.w - dx;
        }

        if (handle.includes("n")) {
          next.y = interaction.startRect.y + dy;
          next.h = interaction.startRect.h - dy;
        }

        if (next.w < 12) {
          if (handle.includes("w")) next.x = interaction.startRect.x + interaction.startRect.w - 12;
          next.w = 12;
        }

        if (next.h < 12) {
          if (handle.includes("n")) next.y = interaction.startRect.y + interaction.startRect.h - 12;
          next.h = 12;
        }

        applyRectChange(interaction.side, next, interaction.exclude);
      }
    };

    const onUp = () => {
      setInteraction(null);
      setGuides({ vertical: null, horizontal: null });
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [interaction, form.CardWidth, form.CardHeight]);

  const beginMoveField = (e, side, idx, field) => {
    e.stopPropagation();
    setSelectedSide(side);
    setSelectedFieldIndex(idx);
    setSelectedObjectKey(null);

    const canvas = canvasRefs[side]?.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();

    setInteraction({
      side,
      mode: "move",
      targetType: "field",
      targetIndex: idx,
      startMouseX: (e.clientX - rect.left) / previewScale,
      startMouseY: (e.clientY - rect.top) / previewScale,
      startRect: getFieldRect(field),
      exclude: { type: "field", idx },
    });
  };

  const beginResizeField = (e, side, idx, field, handle) => {
    e.stopPropagation();
    setSelectedSide(side);
    setSelectedFieldIndex(idx);
    setSelectedObjectKey(null);

    const canvas = canvasRefs[side]?.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();

    setInteraction({
      side,
      mode: "resize",
      handle,
      targetType: "field",
      targetIndex: idx,
      startMouseX: (e.clientX - rect.left) / previewScale,
      startMouseY: (e.clientY - rect.top) / previewScale,
      startRect: getFieldRect(field),
      exclude: { type: "field", idx },
    });
  };

  const beginMoveObject = (e, side, objectKey) => {
    e.stopPropagation();
    setSelectedSide(side);
    setSelectedObjectKey(objectKey);
    setSelectedFieldIndex(null);

    const canvas = canvasRefs[side]?.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const layout = side === "FRONT" ? form.FrontLayoutJson : form.BackLayoutJson;

    setInteraction({
      side,
      mode: "move",
      targetType: "object",
      objectKey,
      startMouseX: (e.clientX - rect.left) / previewScale,
      startMouseY: (e.clientY - rect.top) / previewScale,
      startRect: getObjectRect(layout, objectKey),
      exclude: { type: "object", key: objectKey },
    });
  };

  const beginResizeObject = (e, side, objectKey, handle) => {
    e.stopPropagation();
    setSelectedSide(side);
    setSelectedObjectKey(objectKey);
    setSelectedFieldIndex(null);

    const canvas = canvasRefs[side]?.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const layout = side === "FRONT" ? form.FrontLayoutJson : form.BackLayoutJson;

    setInteraction({
      side,
      mode: "resize",
      handle,
      targetType: "object",
      objectKey,
      startMouseX: (e.clientX - rect.left) / previewScale,
      startMouseY: (e.clientY - rect.top) / previewScale,
      startRect: getObjectRect(layout, objectKey),
      exclude: { type: "object", key: objectKey },
    });
  };

  const ResizeHandles = ({ side, kind, index, rect }) => {
    const points = [
      { key: "nw", left: -5, top: -5, cursor: "nwse-resize" },
      { key: "ne", right: -5, top: -5, cursor: "nesw-resize" },
      { key: "sw", left: -5, bottom: -5, cursor: "nesw-resize" },
      { key: "se", right: -5, bottom: -5, cursor: "nwse-resize" },
      { key: "n", left: "50%", top: -5, transform: "translateX(-50%)", cursor: "ns-resize" },
      { key: "s", left: "50%", bottom: -5, transform: "translateX(-50%)", cursor: "ns-resize" },
      { key: "w", left: -5, top: "50%", transform: "translateY(-50%)", cursor: "ew-resize" },
      { key: "e", right: -5, top: "50%", transform: "translateY(-50%)", cursor: "ew-resize" },
    ];

    return points.map((p) => (
      <Box
        key={`${kind}-${index ?? "obj"}-${p.key}`}
        onMouseDown={(e) => {
          if (kind === "field") beginResizeField(e, side, index, rect, p.key);
          else beginResizeObject(e, side, kind, p.key);
        }}
        sx={{
          position: "absolute",
          width: 10,
          height: 10,
          borderRadius: "50%",
          background: "#fff",
          border: `2px solid ${THEME_ORANGE}`,
          zIndex: 4,
          ...("left" in p ? { left: p.left } : {}),
          ...("right" in p ? { right: p.right } : {}),
          ...("top" in p ? { top: p.top } : {}),
          ...("bottom" in p ? { bottom: p.bottom } : {}),
          ...(p.transform ? { transform: p.transform } : {}),
          cursor: p.cursor,
        }}
      />
    ));
  };

  const DesignObjectFrame = ({
    side,
    objectKey,
    rect,
    selected,
    label,
    onMouseDown,
    children,
    showHandles = true,
    borderStyle = "dashed",
  }) => (
    <Box
      onMouseDown={onMouseDown}
      onMouseEnter={() => setHoveredObjectKey(objectKey)}
     onMouseLeave={() => setHoveredObjectKey(null)}
      onClick={(e) => {
        e.stopPropagation();
        setSelectedSide(side);
        setSelectedObjectKey(objectKey);
        setSelectedFieldIndex(null);
      }}
      sx={{
        position: "absolute",
        left: rect.x,
        top: rect.y,
        width: rect.w,
        height: rect.h,
        border: selected
          ? `2px solid ${THEME_ORANGE}`
          : `1px ${borderStyle} rgba(25,118,210,0.30)`,
        borderRadius: 1,
        cursor: "move",
        zIndex: selected ? 3 : 2,
      }}
    >
      {(selected || hoveredObjectKey === objectKey) && (
        <Box
          sx={{
            position: "absolute",
            top: -20,
            left: 0,
            px: 0.75,
            py: 0.15,
            borderRadius: 1,
            fontSize: 10,
            fontWeight: 800,
            color: selected ? "#fff" : BLUE,
            backgroundColor: selected ? THEME_ORANGE : "rgba(255,255,255,0.88)",
            border: `1px solid ${selected ? THEME_ORANGE : "rgba(25,118,210,0.20)"}`,
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Box>
      )}
      {children}
      {showHandles && selected ? (
        <ResizeHandles side={side} kind={objectKey} rect={rect} />
      ) : null}
    </Box>
  );

  const shiftYUp = (obj, amount) => {
    if (!obj) return obj;
    return {
      ...obj,
      y: Math.max(0, Number(obj.y || 0) - amount),
    };
  };
  
  const handleDeleteObject = (side, objectKey) => {
    updateLayout(side, (layout) => {
      const next = { ...layout };
    
      if (objectKey === "header") {
        const oldHeaderHeight = Number(next.headerHeight ?? 48);
      
        next.headerHeight = 0;
        next.headerDeleted = true;
      
        next.photo = shiftYUp(next.photo, oldHeaderHeight);
        next.logo = shiftYUp(next.logo, oldHeaderHeight);
        next.qr = shiftYUp(next.qr, oldHeaderHeight);
        next.collegeName = shiftYUp(next.collegeName, oldHeaderHeight);
      
        next.fields = (next.fields || []).map((f) =>
          shiftYUp(f, oldHeaderHeight)
        );
      }
    
      if (objectKey === "footer") {
        next.footerHeight = 0;
        next.footerDeleted = true;
        delete next.footer;
      }
    
      if (objectKey === "logo") {
        next.showLogo = false;
        delete next.logo;
      }
    
      if (objectKey === "photo") {
        next.showPhoto = false;
        delete next.photo;
      }
    
      if (objectKey === "qr") {
        next.showQr = false;
        delete next.qr;
      }
    
      if (objectKey === "collegeName") {
        delete next.collegeName;
      }
    
      return next;
    });
  
    setSelectedObjectKey(null);
  };

  // =========================
  // Save
  // =========================
  const handleCreate = async () => {
    try {
      await axios.post(
        `${API_URL}/api/idcards/templates`,
        {
          ...form,
          CampusCode: logincampusCode,
          Orientation: form.Orientation,
          CardWidth: form.CardWidth,
          CardHeight: form.CardHeight,
          FrontLayoutJson: {
            ...form.FrontLayoutJson,
            orientation: form.Orientation,
            cardWidth: form.CardWidth,
            cardHeight: form.CardHeight,
          },
          BackLayoutJson: {
            ...form.BackLayoutJson,
            orientation: form.Orientation,
            cardWidth: form.CardWidth,
            cardHeight: form.CardHeight,
          },
        },
        authConfig
      );

      setSnack({ open: true, message: "Template created", severity: "success" });

      const dims = getDefaultDimensions("PORTRAIT");
      setForm({
        CampusCode: logincampusCode,
        SubCampusCode: "",
        TemplateCode: "",
        TemplateName: "",
        CardType: "STUDENT",
        Orientation: "PORTRAIT",
        CardWidth: dims.CardWidth,
        CardHeight: dims.CardHeight,
        IsDefault: true,
        FrontLayoutJson: getDefaultFrontLayout("STUDENT", "PORTRAIT"),
        BackLayoutJson: getDefaultBackLayout("STUDENT", "PORTRAIT"),
      });
      setSelectedSide("FRONT");
      setSelectedFieldIndex(null);
      setSelectedObjectKey(null);

      await loadTemplates();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to create template");
    }
  };

  // =========================
  // Preview
  // =========================
  const renderPreview = (side) => {
    const layout = side === "FRONT" ? form.FrontLayoutJson : form.BackLayoutJson;
    if (!layout) return null;

    const cardWidth = Number(form.CardWidth || layout.cardWidth || 325);
    const cardHeight = Number(form.CardHeight || layout.cardHeight || 204);
    const isSelectedSide = selectedSide === side;

    return (
      <Box
        sx={{
          display: "inline-flex",
          flexDirection: "column",
          gap: 1,
          alignItems: "center",
        }}
      >
        <Chip
          label={`${side} SIDE`}
          size="small"
          sx={{
            fontWeight: 900,
            color: side === "FRONT" ? THEME_ORANGE : BLUE,
            border: `1px solid ${side === "FRONT" ? "rgba(240,120,40,0.25)" : "rgba(25,118,210,0.25)"}`,
            background: side === "FRONT" ? "rgba(240,120,40,0.06)" : "rgba(25,118,210,0.06)",
          }}
        />

        <Box
          ref={canvasRefs[side]}
          onClick={() => {
            setSelectedSide(side);
            setSelectedFieldIndex(null);
          }}
          sx={{
            position: "relative",
            width: `${cardWidth}px`,
            height: `${cardHeight}px`,
            borderRadius: "16px",
            overflow: "visible",
            border: `2px solid ${layout.accentColor || "#1565C0"}`,
            backgroundColor: "#fff",
            //backgroundImage: layout.backgroundImage ? `url(${layout.backgroundImage})` : "none",
            //backgroundImage: layout.backgroundImage ? `url(${getAssetUrl(layout.backgroundImage)})` : "none",
            backgroundImage: layout.backgroundImage ? `url("${getAssetUrl(layout.backgroundImage)}")` : "none",
            backgroundSize: "cover",
            backgroundPosition: "center",
            boxShadow: isSelectedSide
              ? "0 14px 34px rgba(240,120,40,0.14)"
              : "0 10px 26px rgba(15,23,42,0.10)",
            userSelect: "none",
          }}
        >
          {/* Header */}
          {Number(layout.headerHeight ?? 48) > 0 ? (
            <DesignObjectFrame
              side={side}
              objectKey="header"
              rect={{ x: 0, y: 0, w: cardWidth, h: Number(layout.headerHeight ?? 48) }}
              selected={selectedObjectKey === "header" && selectedSide === side}
              label="Header"
              onMouseDown={(e) => beginMoveObject(e, side, "header")}
              borderStyle="solid"
              showHandles={true}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background: layout.headerColor || THEME_ORANGE,
                  borderTopLeftRadius: "14px",
                  borderTopRightRadius: "14px",
                }}
              />
            </DesignObjectFrame>
          ) : null}

          {/* Background actual visuals */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: "14px",
              overflow: "hidden",
              pointerEvents: "none",
            }}
          >
            {Number(layout.headerHeight ?? 48) > 0 ? (
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  width: "100%",
                  height: Number(layout.headerHeight ?? 48),
                  background: layout.headerColor || THEME_ORANGE,
                }}
              />
            ) : null}
            
            {Number(layout.footerHeight || 0) > 0 ? (
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  bottom: 0,
                  width: "100%",
                  height: Number(layout.footerHeight ?? 28),
                  background: layout.footerColor || BLUE,
                }}
              />
            ) : null}

          </Box>

          {/* Logo */}
          {layout.showLogo ? (
            <DesignObjectFrame
              side={side}
              objectKey="logo"
              rect={getObjectRect(layout, "logo")}
              selected={selectedObjectKey === "logo" && selectedSide === side}
              label="Logo"
              onMouseDown={(e) => beginMoveObject(e, side, "logo")}
            >
              {layout.logoImage ? (
                <Box
                  component="img"
                  //src={layout.logoImage}
                  src={getAssetUrl(layout.logoImage)}
                  alt="logo"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    borderRadius: 1,
                    backgroundColor: "rgba(255,255,255,0.78)",
                    p: 0.25,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(255,255,255,0.78)",
                    color: BLUE,
                    fontWeight: 800,
                    fontSize: 10,
                  }}
                >
                  LOGO
                </Box>
              )}
            </DesignObjectFrame>
          ) : null}

          {/* College Name */}
          <DesignObjectFrame
            side={side}
            objectKey="collegeName"
            rect={getObjectRect(layout, "collegeName")}
            selected={selectedObjectKey === "collegeName" && selectedSide === side}
            label="College Name"
            onMouseDown={(e) => beginMoveObject(e, side, "collegeName")}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",
                px: 0.25,
                display: "-webkit-box",
                WebkitLineClamp: layout.collegeName?.lineClamp ?? 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textAlign: layout.collegeName?.align || "center",
                lineHeight: 1.1,
                color: layout.collegeName?.color || "#fff",
                fontSize: Number(layout.collegeName?.fontSize ?? 13),
                fontWeight: Number(layout.collegeName?.fontWeight ?? 800),
                wordBreak: "break-word",
              }}
            >
              {SAMPLE_DATA.CampusName}
            </Box>
          </DesignObjectFrame>

          {/* Footer */}
          {Number(layout.footerHeight || 0) > 0 && layout.footer ? (
            <DesignObjectFrame
              side={side}
              objectKey="footer"
              rect={getObjectRect(layout, "footer")}
              selected={selectedObjectKey === "footer" && selectedSide === side}
              label="Footer"
              onMouseDown={(e) => beginMoveObject(e, side, "footer")}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  px: 0.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent:
                    layout.footer?.align === "left"
                      ? "flex-start"
                      : layout.footer?.align === "right"
                      ? "flex-end"
                      : "center",
                  textAlign: layout.footer?.align || "center",
                  color: layout.footer?.color || "#FFFFFF",
                  fontSize: Number(layout.footer?.fontSize ?? 10),
                  fontWeight: Number(layout.footer?.fontWeight ?? 700),
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
              >
                {layout.footer?.text ?? ""}
              </Box>
            </DesignObjectFrame>
          ) : null}

          {/* Photo */}
          {side === "FRONT" && layout.showPhoto !== false ? (
            <DesignObjectFrame
              side={side}
              objectKey="photo"
              rect={getObjectRect(layout, "photo")}
              selected={selectedObjectKey === "photo" && selectedSide === side}
              label="Passport Photo"
              onMouseDown={(e) => beginMoveObject(e, side, "photo")}
            >
              {getPassportPhotoUrl(SAMPLE_DATA.PassportPhoto) ? (
                <Box
                  component="img"
                  src={getPassportPhotoUrl(SAMPLE_DATA.PassportPhoto)}
                  alt="student"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: 1.5,
                    border: `2px solid ${layout.accentColor || BLUE}`,
                    display: "block",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    borderRadius: 1.5,
                    border: `2px solid ${layout.accentColor || BLUE}`,
                    background: "linear-gradient(180deg, #f7f7f7, #ededed)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#666",
                    fontWeight: 800,
                    fontSize: 12,
                    overflow: "hidden",
                  }}
                >
                  PHOTO
                </Box>
              )}
            </DesignObjectFrame>
          ) : null}

          {/* QR */}
          {layout.showQr ? (
            <DesignObjectFrame
              side={side}
              objectKey="qr"
              rect={getObjectRect(layout, "qr")}
              selected={selectedObjectKey === "qr" && selectedSide === side}
              label="QR"
              onMouseDown={(e) => beginMoveObject(e, side, "qr")}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  borderRadius: 1,
                  border: "1px solid rgba(0,0,0,0.12)",
                  background:
                    "repeating-linear-gradient(45deg, #111 0px, #111 4px, #fff 4px, #fff 8px)",
                }}
              />
            </DesignObjectFrame>
          ) : null}

          {/* Free Texts */}
          {/*
          {(layout.freeTexts || []).map((item, idx) => {
            const selected = selectedSide === side && selectedObjectKey === `freeText:${idx}`;
          
            return (
              <DesignObjectFrame
                key={`${side}-freeText-${item.id || idx}`}
                side={side}
                objectKey={`freeText:${idx}`}
                rect={item}
                selected={selected}
                label="Free Text"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setSelectedSide(side);
                  setSelectedObjectKey(`freeText:${idx}`);
                  setSelectedFieldIndex(null);
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent:
                      item.align === "left"
                        ? "flex-start"
                        : item.align === "right"
                        ? "flex-end"
                        : "center",
                    color: item.color || "#1F2937",
                    fontSize: Number(item.fontSize || 12),
                    fontWeight: Number(item.fontWeight || 700),
                    overflow: "hidden",
                    textAlign: item.align || "center",
                    px: 0.5,
                  }}
                >
                  {item.text || "Free Text"}
                </Box>
              </DesignObjectFrame>
            );
          })}
          */}
          {/* Free Images */}
          {/*
          {(layout.freeImages || []).map((item, idx) => {
            const selected = selectedSide === side && selectedObjectKey === `freeImage:${idx}`;
          
            return (
              <DesignObjectFrame
                key={`${side}-freeImage-${item.id || idx}`}
                side={side}
                objectKey={`freeImage:${idx}`}
                rect={item}
                selected={selected}
                label="Free Image"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setSelectedSide(side);
                  setSelectedObjectKey(`freeImage:${idx}`);
                  setSelectedFieldIndex(null);
                }}
              >
                {item.image ? (
                  <Box
                    component="img"
                    src={item.image}
                    alt="free"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "rgba(25,118,210,0.06)",
                      color: BLUE,
                      fontWeight: 900,
                      fontSize: 10,
                    }}
                  >
                    IMAGE
                  </Box>
                )}
              </DesignObjectFrame>
            );
          })}          
          */}
          {/* Fields */}
          {(layout.fields || []).map((field, idx) => {
            if (!field.visible) return null;
            const selected = selectedSide === side && selectedFieldIndex === idx;
            const rect = getFieldRect(field);

            return (
              <Box
                key={`${side}-${field.key}-${idx}`}
                onMouseDown={(e) => beginMoveField(e, side, idx, field)}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedSide(side);
                  setSelectedFieldIndex(idx);
                  setSelectedObjectKey(null);
                }}
                sx={{
                  position: "absolute",
                  left: rect.x,
                  top: rect.y,
                  width: rect.w,
                  height: rect.h,
                  px: 0.35,
                  py: 0.15,
                  border: selected
                    ? `2px solid ${THEME_ORANGE}`
                    : "1px dashed rgba(25,118,210,0.18)",
                  borderRadius: 1.2,
                  cursor: "move",
                  backgroundColor: selected
                    ? "rgba(240,120,40,0.08)"
                    : "rgba(255,255,255,0.22)",
                  color: field.color || layout.textColor || "#1F2937",
                  fontSize: Number(field.fontSize || 11),
                  fontWeight: Number(field.fontWeight || 500),
                  lineHeight: 1.15,
                  wordBreak: "break-word",
                  overflow: "hidden",
                  zIndex: selected ? 3 : 2,
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: -20,
                    left: 0,
                    px: 0.75,
                    py: 0.15,
                    borderRadius: 1,
                    fontSize: 10,
                    fontWeight: 800,
                    color: selected ? "#fff" : BLUE,
                    backgroundColor: selected ? THEME_ORANGE : "rgba(255,255,255,0.88)",
                    border: `1px solid ${selected ? THEME_ORANGE : "rgba(25,118,210,0.20)"}`,
                    whiteSpace: "nowrap",
                  }}
                >
                  {field.key}
                </Box>

                {field.type === "FREE_IMAGE" ? (
                  field.image ? (
                    <Box
                      component="img"
                      src={getAssetUrl(field.image)}
                      alt="free"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        display: "block",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 900,
                        color: BLUE,
                      }}
                    >
                      IMAGE
                    </Box>
                  )
                ) : field.type === "FREE_TEXT" ? (
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      overflow: "hidden",
                    }}
                  >
                    {field.value || "Free Text"}
                  </Box>
                ) : (
                  <>
                    <span style={{ fontWeight: 700 }}>
                      {field.label ? `${field.label}: ` : ""}
                    </span>
                    {sampleFieldValue(field.key)}
                  </>
                )}

                {selected ? (
                  <ResizeHandles side={side} kind="field" index={idx} rect={field} />
                ) : null}
              </Box>
            );
          })}

          {/* Helper guides */}
          {selectedSide === side && guides.vertical !== null ? (
            <Box
              sx={{
                position: "absolute",
                left: guides.vertical,
                top: 0,
                width: 1,
                height: cardHeight,
                background: "rgba(240,120,40,0.85)",
                zIndex: 5,
                pointerEvents: "none",
              }}
            />
          ) : null}

          {selectedSide === side && guides.horizontal !== null ? (
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: guides.horizontal,
                width: cardWidth,
                height: 1,
                background: "rgba(240,120,40,0.85)",
                zIndex: 5,
                pointerEvents: "none",
              }}
            />
          ) : null}
        </Box>
      </Box>
    );
  };

  // =========================
  // UI
  // =========================
  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.12rem", fontWeight: 900, lineHeight: 1.2 }}>
              ID Card Template Designer
            </Typography>
            <Typography sx={{ opacity: 0.95, fontSize: "0.86rem" }}>
              Resize, drag, align, style, upload assets, and preview the complete ID card template with sample data
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              sx={{
                ...outlinedBtnSx,
                color: "#fff",
                borderColor: "rgba(255,255,255,0.45)",
                "&:hover": {
                  borderColor: "#fff",
                  backgroundColor: "rgba(255,255,255,0.10)",
                },
              }}
              onClick={loadTemplates}
            >
              Refresh
            </Button>
          </Stack>
        </Box>

        <Box sx={bodySx}>
          {!!error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
                mb: 2,
              }}
            >
              {error}
            </Alert>
          )}

          {!!snack.open && (
            <Snackbar
              open
              autoHideDuration={3000}
              onClose={() => setSnack((s) => ({ ...s, open: false }))}
            >
              <Alert severity={snack.severity} sx={{ borderRadius: 2 }}>
                {snack.message}
              </Alert>
            </Snackbar>
          )}

          {/* Template Basics */}
          <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <LayersIcon sx={{ color: BLUE }} />
              <Typography sx={{ fontWeight: 900, color: BLUE }}>
                Template Basics
              </Typography>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} md={2.5}>
                <TextField
                  fullWidth
                  label="Template Code"
                  value={form.TemplateCode}
                  onChange={(e) => setForm((s) => ({ ...s, TemplateCode: e.target.value }))}
                  size="small"
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} md={2.5}>
                <TextField
                  fullWidth
                  label="Template Name"
                  value={form.TemplateName}
                  onChange={(e) => setForm((s) => ({ ...s, TemplateName: e.target.value }))}
                  size="small"
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  select
                  fullWidth
                  label="Card Type"
                  value={form.CardType}
                  onChange={(e) => handleCardTypeChange(e.target.value)}
                  size="small"
                  sx={inputSx}
                >
                  <MenuItem value="STUDENT">Student</MenuItem>
                  <MenuItem value="EMPLOYEE">Employee</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={1.5}>
                <TextField
                  select
                  fullWidth
                  label="Sub Campus"
                  value={form.SubCampusCode || ""}
                  onChange={(e) =>
                    setForm((s) => ({
                      ...s,
                      SubCampusCode: e.target.value,
                    }))
                  }
                  size="small"
                  sx={inputSx}
                  disabled={loadingLookups}
                >
                  <MenuItem value="">
                    <em>All / Default</em>
                  </MenuItem>
              
                  {options.SubCampusCode.map((sc, index) => {
                    const value = getSubCampusValue(sc);

                    if (!value) return null;

                    return (
                      <MenuItem key={`${value}-${index}`} value={value}>
                        {value}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>

              <Grid item xs={12} md={1.5}>
                <TextField
                  select
                  fullWidth
                  label="Orientation"
                  value={form.Orientation}
                  onChange={(e) => handleOrientationChange(e.target.value)}
                  size="small"
                  sx={inputSx}
                >
                  <MenuItem value="PORTRAIT">Portrait</MenuItem>
                  <MenuItem value="LANDSCAPE">Landscape</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={6} md={1}>
                <TextField
                  fullWidth
                  label="Width"
                  type="number"
                  value={form.CardWidth}
                  onChange={(e) =>
                    updateFormCardSize(Number(e.target.value || 0), Number(form.CardHeight || 0))
                  }
                  size="small"
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={6} md={1}>
                <TextField
                  fullWidth
                  label="Height"
                  type="number"
                  value={form.CardHeight}
                  onChange={(e) =>
                    updateFormCardSize(Number(form.CardWidth || 0), Number(e.target.value || 0))
                  }
                  size="small"
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  alignItems={{ xs: "flex-start", md: "center" }}
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Switch
                      checked={!!form.IsDefault}
                      onChange={(e) => setForm((s) => ({ ...s, IsDefault: e.target.checked }))}
                    />
                    <Typography sx={{ fontWeight: 700 }}>
                      Set as default template
                    </Typography>
                  </Stack>
              
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    flexWrap="wrap"
                    justifyContent={{ xs: "flex-start", md: "flex-end" }}
                  >
                    <Button variant="contained" sx={primaryBtnSx} onClick={handleCreate}>
                      Create Template
                    </Button>
              
                    <Button variant="outlined" sx={outlinedBtnSx} onClick={loadTemplates}>
                      Refresh
                    </Button>
              
                    <Button
                      variant="outlined"
                      sx={outlinedBtnSx}
                      onClick={() => {
                        const dims = getDefaultDimensions("PORTRAIT");
                        setForm({
                          CampusCode: logincampusCode,
                          SubCampusCode: "",
                          TemplateCode: "",
                          TemplateName: "",
                          CardType: "STUDENT",
                          Orientation: "PORTRAIT",
                          CardWidth: dims.CardWidth,
                          CardHeight: dims.CardHeight,
                          IsDefault: true,
                          FrontLayoutJson: getDefaultFrontLayout("STUDENT", "PORTRAIT"),
                          BackLayoutJson: getDefaultBackLayout("STUDENT", "PORTRAIT"),
                        });
                        setSelectedSide("FRONT");
                        setSelectedFieldIndex(null);
                        setSelectedObjectKey(null);
                      }}
                    >
                      Reset Designer
                    </Button>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          {/* Designer Area */}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {/* Left Controls */}
            <Grid item xs={12} xl={5}>
              <Stack spacing={2}>
                <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography sx={{ fontWeight: 900, color: BLUE }}>
                      Side Designer
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant={selectedSide === "FRONT" ? "contained" : "outlined"}
                        sx={selectedSide === "FRONT" ? primaryBtnSx : outlinedBtnSx}
                        onClick={() => {
                          setSelectedSide("FRONT");
                          setSelectedFieldIndex(null);
                        }}
                      >
                        Front
                      </Button>
                      <Button
                        variant={selectedSide === "BACK" ? "contained" : "outlined"}
                        sx={selectedSide === "BACK" ? primaryBtnSx : outlinedBtnSx}
                        onClick={() => {
                          setSelectedSide("BACK");
                          setSelectedFieldIndex(null);
                        }}
                      >
                        Back
                      </Button>
                    </Stack>
                  </Stack>

                  <Typography sx={{ fontSize: "0.86rem", color: "rgba(25,118,210,0.75)", mb: 1.5 }}>
                    Drag any field/object inside preview. Resize using handles. Orange guide lines appear when aligned near other elements.
                  </Typography>

                  <Divider sx={{ mb: 1.5 }} />

                  <Typography sx={{ fontWeight: 900, mb: 1, color: BLUE }}>
                    Add Fields
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: "wrap" }}>
                    {AVAILABLE_FIELDS[form.CardType].map((f) => (
                      <Button
                        key={`${selectedSide}-${f}`}
                        variant="outlined"
                        size="small"
                        sx={outlinedBtnSx}
                        startIcon={<AddIcon />}
                        onClick={() => handleAddField(f)}
                      >
                        {f}
                      </Button>
                    ))}
                  </Stack>

                  <Typography sx={{ fontWeight: 900, mb: 1, color: BLUE }}>
                    Select Object
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {SIDE_OBJECT_OPTIONS.map((o) => (
                      <Chip
                        key={o.key}
                        icon={o.icon}
                        label={o.label}
                        clickable
                        onClick={() => {
                          if (o.key === "freeText") {
                            handleAddFreeText();
                            return;
                          }
                        
                          if (o.key === "freeImage") {
                            handleAddFreeImage();
                            return;
                          }
                        
                          updateLayout(selectedSide, (layout) =>
                            ensureObjectExists(layout, o.key, selectedSide, form.Orientation)
                          );
                          setSelectedObjectKey(o.key);
                          setSelectedFieldIndex(null);
                        }}
                        sx={{
                          mb: 1,
                          fontWeight: 800,
                          border:
                            selectedObjectKey === o.key
                              ? `1px solid ${THEME_ORANGE}`
                              : "1px solid rgba(25,118,210,0.18)",
                          background:
                            selectedObjectKey === o.key
                              ? "rgba(240,120,40,0.08)"
                              : "#fff",
                          color:
                            selectedObjectKey === o.key ? THEME_ORANGE : BLUE,
                        }}
                      />
                    ))}
                  </Stack>
                </Paper>

                {/* Styling */}
                <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
                  <Typography sx={{ fontWeight: 900, mb: 1.5, color: BLUE }}>
                    {selectedSide} Side Styling / Assets
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      
                      <TextField
                        fullWidth
                        label="Header Color"
                        type="color"
                        value={currentLayout?.headerColor || "#000000"}
                        onChange={(e) =>
                          updateLayout(selectedSide, (layout) => ({
                            ...layout,
                            headerColor: e.target.value,
                          }))
                        }
                        size="small"
                        sx={{
                          ...inputSx,
                          "& input": {
                            height: 40,
                            cursor: "pointer",
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Footer Color"
                        type="color"
                        value={currentLayout?.footerColor || "#000000"}
                        onChange={(e) =>
                          updateLayout(selectedSide, (layout) => ({
                            ...layout,
                            footerColor: e.target.value,
                          }))
                        }
                        size="small"
                        sx={{
                          ...inputSx,
                          "& input": { height: 40, cursor: "pointer" },
                        }}
                      />
                      
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Border / Accent"
                        type="color"
                        value={currentLayout?.accentColor || "#000000"}
                        onChange={(e) =>
                          updateLayout(selectedSide, (layout) => ({
                            ...layout,
                            accentColor: e.target.value,
                          }))
                        }
                        size="small"
                        sx={{
                          ...inputSx,
                          "& input": { height: 40, cursor: "pointer" },
                        }}
                      />
                      
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Default Text Color"
                        type="color"
                        value={currentLayout?.textColor || "#000000"}
                        onChange={(e) =>
                          updateLayout(selectedSide, (layout) => ({
                            ...layout,
                            textColor: e.target.value,
                          }))
                        }
                        size="small"
                        sx={{
                          ...inputSx,
                          "& input": { height: 40, cursor: "pointer" },
                        }}
                      />
                      
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Button
                        component="label"
                        variant="outlined"
                        sx={{ ...outlinedBtnSx, width: "100%", height: 40 }}
                      >
                        Upload Background
                        <input
                          hidden
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(selectedSide, "backgroundImage", e.target.files?.[0])}
                        />
                      </Button>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Button
                        component="label"
                        variant="outlined"
                        sx={{ ...outlinedBtnSx, width: "100%", height: 40 }}
                      >
                        Upload College Logo
                        <input
                          hidden
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(selectedSide, "logoImage", e.target.files?.[0])}
                        />
                      </Button>
                    </Grid>

                    <Grid item xs={12}>
                      <Stack direction="row" spacing={2} flexWrap="wrap">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Switch
                            checked={!!currentLayout?.showLogo}
                            onChange={(e) =>
                              updateLayout(selectedSide, (layout) => ({
                                ...layout,
                                showLogo: e.target.checked,
                              }))
                            }
                          />
                          <Typography sx={{ fontWeight: 700 }}>Show Logo</Typography>
                        </Stack>

                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Switch
                            checked={!!currentLayout?.showPhoto}
                            onChange={(e) =>
                              updateLayout(selectedSide, (layout) => ({
                                ...layout,
                                showPhoto: e.target.checked,
                              }))
                            }
                          />
                          <Typography sx={{ fontWeight: 700 }}>Show Photo</Typography>
                        </Stack>

                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Switch
                            checked={!!currentLayout?.showQr}
                            onChange={(e) =>
                              updateLayout(selectedSide, (layout) => ({
                                ...layout,
                                showQr: e.target.checked,
                              }))
                            }
                          />
                          <Typography sx={{ fontWeight: 700 }}>Show QR</Typography>
                        </Stack>
                      </Stack>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Properties Panel */}
                <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
                  <Typography sx={{ fontWeight: 900, mb: 1.5, color: BLUE }}>
                    Properties Panel
                  </Typography>

                  {selectedFieldIndex !== null ? (
                    <Grid container spacing={1.25}>
                      <Grid item xs={12}>
                        <Chip
                          label={`Selected Field: ${currentFields[selectedFieldIndex]?.key || ""}`}
                          sx={{
                            fontWeight: 900,
                            background: "rgba(240,120,40,0.08)",
                            color: THEME_ORANGE,
                            border: "1px solid rgba(240,120,40,0.20)",
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Label"
                          value={currentFields[selectedFieldIndex]?.label || ""}
                          onChange={(e) =>
                            handleSideFieldChange(selectedFieldIndex, "label", e.target.value)
                          }
                          size="small"
                          sx={inputSx}
                        />
                      </Grid>

                      {["x", "y", "w", "h"].map((k) => (
                        <Grid item xs={6} md={3} key={`field-${k}`}>
                          <TextField
                            fullWidth
                            label={k.toUpperCase()}
                            type="number"
                            value={currentFields[selectedFieldIndex]?.[k] ?? 0}
                            onChange={(e) =>
                              handleSideFieldChange(
                                selectedFieldIndex,
                                k,
                                Number(e.target.value)
                              )
                            }
                            size="small"
                            sx={inputSx}
                          />
                        </Grid>
                        
                      ))}

                      <Grid item xs={6} md={4}>
                        <TextField
                          fullWidth
                          label="Font Size"
                          type="number"
                          value={currentFields[selectedFieldIndex]?.fontSize ?? 11}
                          onChange={(e) =>
                            handleSideFieldChange(
                              selectedFieldIndex,
                              "fontSize",
                              Number(e.target.value)
                            )
                          }
                          size="small"
                          sx={inputSx}
                        />
                      </Grid>

                      <Grid item xs={6} md={4}>
                        <TextField
                          fullWidth
                          label="Font Weight"
                          type="number"
                          value={currentFields[selectedFieldIndex]?.fontWeight ?? 500}
                          onChange={(e) =>
                            handleSideFieldChange(
                              selectedFieldIndex,
                              "fontWeight",
                              Number(e.target.value)
                            )
                          }
                          size="small"
                          sx={inputSx}
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField
                          select
                          fullWidth
                          label="Visible"
                          value={currentFields[selectedFieldIndex]?.visible ? "Y" : "N"}
                          onChange={(e) =>
                            handleSideFieldChange(
                              selectedFieldIndex,
                              "visible",
                              e.target.value === "Y"
                            )
                          }
                          size="small"
                          sx={inputSx}
                        >
                          <MenuItem value="Y">Yes</MenuItem>
                          <MenuItem value="N">No</MenuItem>
                        </TextField>
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Field Color"
                          type="color"
                          value={
                            currentFields[selectedFieldIndex]?.color ||
                            currentLayout?.textColor ||
                            "#1F2937"
                          }
                          onChange={(e) =>
                            handleSideFieldChange(selectedFieldIndex, "color", e.target.value)
                          }
                          size="small"
                          sx={{
                            ...inputSx,
                            "& input": {
                              height: 40,
                              cursor: "pointer",
                            },
                          }}
                        />
                        
                      </Grid>

                      {currentFields[selectedFieldIndex]?.type === "FREE_TEXT" && (
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Free Text"
                            value={currentFields[selectedFieldIndex]?.value || ""}
                            onChange={(e) =>
                              handleSideFieldChange(selectedFieldIndex, "value", e.target.value)
                            }
                            size="small"
                            sx={inputSx}
                          />
                        </Grid>
                      )}
                      
                      {currentFields[selectedFieldIndex]?.type === "FREE_IMAGE" && (
                        <Grid item xs={12}>
                          <Button
                            component="label"
                            variant="outlined"
                            sx={{ ...outlinedBtnSx, width: "100%", height: 40 }}
                          >
                            Upload Free Image
                            <input
                              hidden
                              type="file"
                              accept="image/*"
                              onChange={async (e) => {
                                try {
                                  const file = e.target.files?.[0];
                                  const filename = await uploadTemplateAsset(file, selectedSide, "FREE_IMAGE");
                                
                                  if (!filename) return;
                                
                                  handleSideFieldChange(selectedFieldIndex, "image", filename);
                                
                                  setSnack({
                                    open: true,
                                    message: "Free image uploaded successfully",
                                    severity: "success",
                                  });
                                } catch (err) {
                                  console.error(err);
                                  setError(err?.response?.data?.error || "Failed to upload free image");
                                } finally {
                                  e.target.value = "";
                                }
                              }}
                            />
                          </Button>
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        <Button
                          fullWidth
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleRemoveField(selectedFieldIndex)}
                          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}
                        >
                          Remove Field
                        </Button>
                      </Grid>
                    </Grid>
                  ) : selectedObjectKey ? (
                    <Grid container spacing={1.25}>
                      <Grid item xs={12}>
                        <Chip
                          label={`Selected Object: ${selectedObjectKey}`}
                          sx={{
                            fontWeight: 900,
                            background: "rgba(25,118,210,0.08)",
                            color: BLUE,
                            border: "1px solid rgba(25,118,210,0.20)",
                          }}
                        />
                      </Grid>

                      {selectedObjectKey === "header" ? (
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Header Height"
                            type="number"
                            value={currentLayout?.headerHeight ?? 48}
                            onChange={(e) =>
                              updateLayout(selectedSide, (layout) => ({
                                ...layout,
                                headerHeight: Number(e.target.value),
                              }))
                            }
                            size="small"
                            sx={inputSx}
                          />
                        </Grid>
                      ) : (
                        <>
                          {["x", "y", "w", "h"].map((k) => (
                            <Grid item xs={6} md={3} key={`${selectedObjectKey}-${k}`}>
                              <TextField
                                fullWidth
                                label={k.toUpperCase()}
                                type="number"
                                value={currentLayout?.[selectedObjectKey]?.[k] ?? 0}
                                onChange={(e) =>
                                  updateLayout(selectedSide, (layout) => ({
                                    ...layout,
                                    [selectedObjectKey]: {
                                      ...(layout[selectedObjectKey] || {}),
                                      [k]: Number(e.target.value),
                                    },
                                  }))
                                }
                                size="small"
                                sx={inputSx}
                              />
                            </Grid>
                          ))}
                        </>
                      )}

                      {selectedObjectKey === "collegeName" ? (
                        <>
                          <Grid item xs={6} md={4}>
                            <TextField
                              fullWidth
                              label="Font Size"
                              type="number"
                              value={currentLayout?.collegeName?.fontSize ?? 13}
                              onChange={(e) =>
                                updateLayout(selectedSide, (layout) => ({
                                  ...layout,
                                  collegeName: {
                                    ...(layout.collegeName || {}),
                                    fontSize: Number(e.target.value),
                                  },
                                }))
                              }
                              size="small"
                              sx={inputSx}
                            />
                          </Grid>

                          <Grid item xs={6} md={4}>
                            <TextField
                              fullWidth
                              label="Font Weight"
                              type="number"
                              value={currentLayout?.collegeName?.fontWeight ?? 800}
                              onChange={(e) =>
                                updateLayout(selectedSide, (layout) => ({
                                  ...layout,
                                  collegeName: {
                                    ...(layout.collegeName || {}),
                                    fontWeight: Number(e.target.value),
                                  },
                                }))
                              }
                              size="small"
                              sx={inputSx}
                            />
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="Text Color"
                              type="color"
                              value={currentLayout?.collegeName?.color || "#FFFFFF"}
                              onChange={(e) =>
                                updateLayout(selectedSide, (layout) => ({
                                  ...layout,
                                  collegeName: {
                                    ...(layout.collegeName || {}),
                                    color: e.target.value,
                                  },
                                }))
                              }
                              size="small"
                              sx={{
                                ...inputSx,
                                "& input": {
                                  height: 40,
                                  cursor: "pointer",
                                },
                              }}
                            />
                            
                          </Grid>
                        </>
                      ) : null}

                      {selectedObjectKey === "footer" ? (
                        <>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Footer Text"
                              value={currentLayout?.footer?.text || ""}
                              onChange={(e) =>
                                updateLayout(selectedSide, (layout) => ({
                                  ...layout,
                                  footer: {
                                    ...(layout.footer || {}),
                                    text: e.target.value,
                                  },
                                }))
                              }
                              size="small"
                              sx={inputSx}
                            />
                          </Grid>
                      
                          <Grid item xs={6} md={4}>
                            <TextField
                              fullWidth
                              label="Font Size"
                              type="number"
                              value={currentLayout?.footer?.fontSize ?? 10}
                              onChange={(e) =>
                                updateLayout(selectedSide, (layout) => ({
                                  ...layout,
                                  footer: {
                                    ...(layout.footer || {}),
                                    fontSize: Number(e.target.value),
                                  },
                                }))
                              }
                              size="small"
                              sx={inputSx}
                            />
                          </Grid>
                      
                          <Grid item xs={6} md={4}>
                            <TextField
                              fullWidth
                              label="Font Weight"
                              type="number"
                              value={currentLayout?.footer?.fontWeight ?? 700}
                              onChange={(e) =>
                                updateLayout(selectedSide, (layout) => ({
                                  ...layout,
                                  footer: {
                                    ...(layout.footer || {}),
                                    fontWeight: Number(e.target.value),
                                  },
                                }))
                              }
                              size="small"
                              sx={inputSx}
                            />
                          </Grid>
                      
                          <Grid item xs={12} md={4}>
                            <TextField
                              select
                              fullWidth
                              label="Align"
                              value={currentLayout?.footer?.align || "center"}
                              onChange={(e) =>
                                updateLayout(selectedSide, (layout) => ({
                                  ...layout,
                                  footer: {
                                    ...(layout.footer || {}),
                                    align: e.target.value,
                                  },
                                }))
                              }
                              size="small"
                              sx={inputSx}
                            >
                              <MenuItem value="left">Left</MenuItem>
                              <MenuItem value="center">Center</MenuItem>
                              <MenuItem value="right">Right</MenuItem>
                            </TextField>
                          </Grid>
                      
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Footer Text Color"
                              type="color"
                              value={currentLayout?.footer?.color || "#FFFFFF"}
                              onChange={(e) =>
                                updateLayout(selectedSide, (layout) => ({
                                  ...layout,
                                  footer: {
                                    ...(layout.footer || {}),
                                    color: e.target.value,
                                  },
                                }))
                              }
                              size="small"
                              sx={{
                                ...inputSx,
                                "& input": {
                                  height: 40,
                                  cursor: "pointer",
                                },
                              }}
                            />
                            
                          </Grid>
                        </>
                      ) : null}

                        <Grid item xs={12}>
                            <Button
                              fullWidth
                              variant="outlined"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={() => handleDeleteObject(selectedSide, selectedObjectKey)}
                              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}
                            >
                              Delete {selectedObjectKey}
                            </Button>
                          </Grid>

                    </Grid>
                    
                  ) : (
                    <Typography sx={{ color: "rgba(25,118,210,0.75)", fontWeight: 700 }}>
                      Click a field, logo, photo, QR, header, or college name block in preview to edit its properties.
                    </Typography>
                  )}
                </Paper>
              </Stack>
            </Grid>

            {/* Preview */}
            <Grid item xs={12} xl={7}>
              <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontWeight: 900, color: BLUE }}>
                    Live Template Preview
                  </Typography>
                  <Chip
                    icon={<CropFreeIcon />}
                    label="Drag + Resize + Snap Guides"
                    sx={{
                      fontWeight: 800,
                      color: THEME_ORANGE,
                      background: "rgba(240,120,40,0.06)",
                      border: "1px solid rgba(240,120,40,0.18)",
                    }}
                  />
                </Stack>

                <Box
                  sx={{
                    borderRadius: 3,
                    border: "1px solid rgba(25,118,210,0.14)",
                    background:
                      "linear-gradient(180deg, rgba(25,118,210,0.03), rgba(240,120,40,0.02))",
                    p: 2,
                    overflow: "auto",
                  }}
                >
                  <Stack
                    direction={{ xs: "column", lg: "row" }}
                    spacing={4}
                    alignItems="flex-start"
                  >
                    {renderPreview("FRONT")}
                    {renderPreview("BACK")}
                  </Stack>
                </Box>
              </Paper>

              {/* Mini field list */}
              <Paper elevation={0} sx={{ ...surfaceSx, p: 2, mt: 2 }}>
                <Typography sx={{ fontWeight: 900, color: BLUE, mb: 1.25 }}>
                  {selectedSide} Side Elements
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {currentFields.map((field, idx) => (
                    <Chip
                      key={`${selectedSide}-chip-${field.key}-${idx}`}
                      label={`${field.key} (${field.x},${field.y})`}
                      clickable
                      onClick={() => {
                        setSelectedFieldIndex(idx);
                        setSelectedObjectKey(null);
                      }}
                      sx={{
                        mb: 1,
                        fontWeight: 800,
                        border:
                          selectedFieldIndex === idx
                            ? `1px solid ${THEME_ORANGE}`
                            : "1px solid rgba(25,118,210,0.18)",
                        background:
                          selectedFieldIndex === idx
                            ? "rgba(240,120,40,0.08)"
                            : "#fff",
                        color: selectedFieldIndex === idx ? THEME_ORANGE : BLUE,
                      }}
                    />
                  ))}

                  {!currentFields.length && (
                    <Typography sx={{ color: "rgba(25,118,210,0.75)", fontWeight: 700 }}>
                      No fields yet on this side.
                    </Typography>
                  )}
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          {/* Existing templates */}
          <Paper elevation={0} sx={tableWrapSx}>
            <Box sx={{ overflowX: "auto", overflowY: "hidden" }}>
              <TableContainer component="div">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={theadRowSx}>
                      <TableCell>Campus</TableCell>
                      <TableCell>SubCampus</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Orientation</TableCell>
                      <TableCell>Default</TableCell>
                      <TableCell>Active</TableCell>
                      <TableCell>Version</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {rows.map((row, idx) => {
                      const isEdit = editRowId === row.TemplateId;
                  
                      return isEdit ? (
                        <TableRow key={row.TemplateId} sx={zebraRowSx(idx)}>
                          
                          

                          <TableCell>
                            <TextField
                              name="TemplateCode"
                              value={row.TemplateCode || ""}
                              onChange={(e) => handleTemplateInputChange(e, row.TemplateId)}
                              fullWidth
                              size="small"
                              sx={inputSx}
                              disabled
                            />
                          </TableCell>

                          <TableCell>
                            <TextField
                              select
                              name="SubCampusCode"
                              value={row.SubCampusCode || ""}
                              onChange={(e) => handleTemplateInputChange(e, row.TemplateId)}
                              fullWidth
                              size="small"
                              sx={inputSx}
                            >
                              <MenuItem value="">
                                <em>All / Default</em>
                              </MenuItem>
                          
                            {options.SubCampusCode.map((sc, index) => {
                              const value = getSubCampusValue(sc);
                            
                              if (!value) return null;
                            
                              return (
                                <MenuItem key={`${value}-${index}`} value={value}>
                                  {value}
                                </MenuItem>
                              );
                            })}

                            </TextField>
                          </TableCell>
                  
                          <TableCell>
                            <TextField
                              name="TemplateName"
                              value={row.TemplateName || ""}
                              onChange={(e) => handleTemplateInputChange(e, row.TemplateId)}
                              fullWidth
                              size="small"
                              sx={inputSx}
                            />
                          </TableCell>
                  
                          <TableCell>
                            <TextField
                              select
                              name="CardType"
                              value={row.CardType || "STUDENT"}
                              onChange={(e) => handleTemplateInputChange(e, row.TemplateId)}
                              fullWidth
                              size="small"
                              sx={inputSx}
                              disabled
                            >
                              <MenuItem value="STUDENT">Student</MenuItem>
                              <MenuItem value="EMPLOYEE">Employee</MenuItem>
                            </TextField>
                          </TableCell>
                  
                          <TableCell>
                           <TextField
                             select
                             name="Orientation"
                             value={row.Orientation || "PORTRAIT"}
                             onChange={(e) => {
                               const value = e.target.value;
                               handleTemplateInputChange(e, row.TemplateId);
                               handleOrientationChange(value);
                             }}
                             fullWidth
                             size="small"
                             sx={inputSx}
                           > 
                            <MenuItem value="PORTRAIT">Portrait</MenuItem>
                            <MenuItem value="LANDSCAPE">Landscape</MenuItem>
                           </TextField>
                          </TableCell>
                  
                          <TableCell>
                            <Checkbox
                              name="IsDefault"
                              checked={!!row.IsDefault}
                              onChange={(e) => handleTemplateInputChange(e, row.TemplateId)}
                              sx={{
                                color: "rgba(25,118,210,0.75)",
                                "&.Mui-checked": { color: THEME_ORANGE },
                              }}
                            />
                          </TableCell>
                  
                          <TableCell>
                            <Checkbox
                              name="IsActive"
                              checked={!!row.IsActive}
                              onChange={(e) => handleTemplateInputChange(e, row.TemplateId)}
                              sx={{
                                color: "rgba(25,118,210,0.75)",
                                "&.Mui-checked": { color: THEME_ORANGE },
                              }}
                            />
                          </TableCell>
                  
                          <TableCell>{row.VersionNo}</TableCell>
                  
                          <TableCell>
                            <IconButton
                              sx={tinyIconBtnSx}
                              onClick={() => handleUpdateRow(row.TemplateId)}
                              title="Save"
                            >
                              <SaveIcon />
                            </IconButton>
                            <IconButton
                              sx={tinyIconBtnSx}
                              /*onClick={() => setEditRowId(null)}*/
                              onClick={() => {
                                setEditRowId(null);
                                const dims = getDefaultDimensions("PORTRAIT");
                                setForm({
                                  CampusCode: logincampusCode,
                                  SubCampusCode: "",
                                  TemplateCode: "",
                                  TemplateName: "",
                                  CardType: "STUDENT",
                                  Orientation: "PORTRAIT",
                                  CardWidth: dims.CardWidth,
                                  CardHeight: dims.CardHeight,
                                  IsDefault: true,
                                  FrontLayoutJson: getDefaultFrontLayout("STUDENT", "PORTRAIT"),
                                  BackLayoutJson: getDefaultBackLayout("STUDENT", "PORTRAIT"),
                                });
                                setSelectedSide("FRONT");
                                setSelectedFieldIndex(null);
                                setSelectedObjectKey(null);
                              }}
                              title="Cancel"
                            >
                              <CancelIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ) : (
                        <TableRow key={row.TemplateId} sx={zebraRowSx(idx)}>
                        <TableCell>{row.TemplateCode}</TableCell>
                        <TableCell>{row.SubCampusCode}</TableCell>
                        <TableCell>{row.TemplateName}</TableCell>
                        <TableCell>{row.CardType}</TableCell>
                        <TableCell>{row.Orientation}</TableCell>
                      
                        <TableCell>
                          <Checkbox checked={!!row.IsDefault} readOnly sx={{ "&.Mui-checked": { color: THEME_ORANGE } }} />
                        </TableCell>
                      
                        <TableCell>
                          <Checkbox checked={!!row.IsActive} readOnly sx={{ "&.Mui-checked": { color: THEME_ORANGE } }} />
                        </TableCell>
                      
                        <TableCell>{row.VersionNo}</TableCell>
                      
                        <TableCell>
                          <IconButton
                            sx={tinyIconBtnSx}
                            //onClick={() => handleEditTemplate(row)}
                            onClick={async () => {
                              try {
                                const { data } = await axios.get(
                                  `${API_URL}/api/idcards/templates/${row.TemplateId}`,
                                  authConfig
                                );
                              
                                handleEditTemplate(data);
                              } catch (err) {
                                setError(err?.response?.data?.error || "Failed to load template");
                              }
                            }}
                            title="Edit"
                          >
                            <EditIcon />
                          </IconButton>
                      
                          <IconButton
                            sx={tinyIconBtnSx}
                            onClick={() => handleDeleteRow(row.TemplateId)}
                            title="Delete"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                    {!rows.length && (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          No templates found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
};

export default IdCardTemplateManager;